import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, getToken } from '../config';

/**
 * useGradeStream — consumes the backend SSE endpoint POST /ai/grade/stream.
 *
 * State machine:
 *   idle      → before submit()
 *   streaming → request in flight; thinking is accumulating
 *   done      → feedback received
 *   error     → request failed
 *
 * The hook accumulates `thinking` chunks into a single string so the UI can
 * render it as a typewriter effect. Once a `feedback` event arrives, the
 * hook transitions to `done` and the stream is closed.
 */
export function useGradeStream() {
  const [status, setStatus] = useState('idle');
  const [thinking, setThinking] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setStatus('idle');
    setThinking('');
    setFeedback(null);
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setStatus('idle');
  }, []);

  const submit = useCallback(async ({ paragraphId, translation }) => {
    reset();
    setStatus('streaming');

    const controller = new AbortController();
    abortRef.current = controller;

    const token = getToken();

    try {
      const resp = await fetch(`${API_URL}/ai/grade/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ paragraphId, translation }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
          const text = await resp.text();
          if (text) msg = text;
        } catch {
          /* ignore */
        }
        setError(msg);
        setStatus('error');
        return;
      }
      if (!resp.body) {
        setError('No response body — streaming not supported by this browser?');
        setStatus('error');
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buf = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        // SSE events are separated by \n\n. Process complete events from the
        // buffer; keep the partial trailing event for the next read.
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const rawEvent = buf.slice(0, idx);
          buf = buf.slice(idx + 2);

          // An SSE event is one or more lines; we only care about `data: ...`.
          const dataLines = rawEvent
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trimStart());
          if (dataLines.length === 0) continue;

          let parsed;
          try {
            parsed = JSON.parse(dataLines.join('\n'));
          } catch {
            continue;
          }

          if (parsed.type === 'thinking' && parsed.thinking) {
            setThinking((prev) => prev + parsed.thinking);
          } else if (parsed.type === 'feedback' && parsed.feedback) {
            setFeedback(parsed.feedback);
            setStatus('done');
          } else if (parsed.type === 'error') {
            setError(parsed.error || 'Unknown grading error');
            setStatus('error');
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to grade');
      setStatus('error');
    } finally {
      abortRef.current = null;
    }
  }, [reset]);

  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return { status, thinking, feedback, error, submit, cancel, reset };
}
